import { useState } from "react";
import { Printer, Search, FileText } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useProductionMaterials } from "@/hooks/useProductionMaterials";
import { openPrintableCuttingList } from "@/components/production/PrintableCuttingList";
import { CuttingListSkeleton } from "@/components/production/CuttingListSkeleton";
import { formatDimensionMm } from "@/lib/formatters";

export default function CuttingList() {
  const [searchTerm, setSearchTerm] = useState("");
  const { data: materials = [], isLoading } = useProductionMaterials();

  const filteredMaterials = materials.filter((m) => {
    const search = searchTerm.toLowerCase();
    return (
      m.order_number.toLowerCase().includes(search) ||
      (m.material_name?.toLowerCase() || "").includes(search) ||
      (m.component_name?.toLowerCase() || "").includes(search) ||
      (m.source_model_name?.toLowerCase() || "").includes(search) ||
      (m.product_model_name?.toLowerCase() || "").includes(search)
    );
  });

  const handlePrint = () => {
    openPrintableCuttingList({
      title: "Sahauslista",
      items: filteredMaterials.map((m) => ({
        orderNumber: m.order_number,
        componentName: m.component_name,
        materialName: m.material_name,
        materialSku: m.material_sku,
        materialCategory: m.material_category,
        sourceModelName: m.source_model_name,
        productModelName: m.product_model_name,
        quantity: m.quantity,
        /** Width in meters */
        width: m.width,
        /** Height in meters */
        height: m.height,
        /** Thickness in meters */
        thickness: m.thickness,
        notes: m.notes,
        source: m.source,
      })),
      filterOrderNumber: searchTerm || undefined,
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <FileText className="h-8 w-8" />
              Sahauslista
            </h1>
            <p className="text-muted-foreground">
              Tuotannon materiaalilista tilausten käsittelyyn
            </p>
          </div>
          <Button onClick={handlePrint} disabled={filteredMaterials.length === 0}>
            <Printer className="mr-2 h-4 w-4" />
            Tulosta lista
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Hae tilauksella, materiaalilla..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Badge variant="secondary">
            {filteredMaterials.length} materiaalia
          </Badge>
        </div>

        {isLoading ? (
          <CuttingListSkeleton />
        ) : filteredMaterials.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            Ei materiaaleja näytettäväksi
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tilaus</TableHead>
                  <TableHead>Komponentti</TableHead>
                  <TableHead>Materiaali</TableHead>
                  <TableHead className="text-right">Kpl</TableHead>
                  <TableHead className="text-right">Leveys</TableHead>
                  <TableHead className="text-right">Korkeus</TableHead>
                  <TableHead className="text-right">Paksuus</TableHead>
                  <TableHead>Tyyppi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMaterials.map((material, index) => (
                  <TableRow key={`${material.source_id}-${index}`}>
                    <TableCell className="font-medium">
                      {material.order_number}
                    </TableCell>
                    <TableCell>
                      {(material.source_model_name || material.product_model_name) && (
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {material.source_model_name || material.product_model_name}
                        </div>
                      )}
                      {material.component_name || "-"}
                      {material.notes && (
                        <div className="text-xs text-muted-foreground">
                          {material.notes}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {material.material_name || "-"}
                      {material.material_sku && (
                        <div className="text-xs text-muted-foreground">
                          {material.material_sku}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {material.quantity}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatDimensionMm(material.width)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatDimensionMm(material.height)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatDimensionMm(material.thickness)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          material.source === "configuration"
                            ? "default"
                            : "outline"
                        }
                      >
                        {material.source === "configuration"
                          ? "Konfiguraatio"
                          : "Manuaalinen"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </Layout>
  );
}
